import React from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { I18nProvider } from "@/context/I18nContext";
import { AppShell } from "@/components/shell/AppShell";
import { Toaster } from "@/components/ui/sonner";
import Login from "@/pages/Login";
import Activate from "@/pages/Activate";
import Notifications from "@/pages/Notifications";
import Search from "@/pages/Search";
import Today from "@/pages/Today";
import Stores from "@/pages/Stores";
import StoreDay from "@/pages/StoreDay";
import Actions from "@/pages/Actions";
import Inbox from "@/pages/Inbox";
import Communications from "@/pages/Communications";
import Knowledge from "@/pages/Knowledge";
import Tasks from "@/pages/Tasks";
import MyWork from "@/pages/MyWork";
import Learning from "@/pages/Learning";
import Scheduling from "@/pages/Scheduling";
import MySchedule from "@/pages/MySchedule";
import MyDay from "@/pages/MyDay";
import UnifiedCalendar from "@/pages/UnifiedCalendar";
import Conversations from "@/pages/Conversations";
import Forms from "@/pages/Forms";
import Freshness from "@/pages/Freshness";
import LaborPool from "@/pages/LaborPool";
import Capacity from "@/pages/Capacity";
import OperatingHours from "@/pages/OperatingHours";
import TimeAttendance from "@/pages/TimeAttendance";
import MyTime from "@/pages/MyTime";
import LaborPlanning from "@/pages/LaborPlanning";
import Assure from "@/pages/Assure";
import Intelligence from "@/pages/Intelligence";
import ForecastAdjustments from "@/pages/ForecastAdjustments";
import HelpDesk from "@/pages/HelpDesk";
import Assist from "@/pages/Assist";
import Connect from "@/pages/Connect";
import Platform from "@/pages/Platform";
import Placeholder from "@/pages/Placeholder";
import Demo from "@/pages/Demo";
import Hierarchy from "@/pages/admin/Hierarchy";
import Users from "@/pages/admin/Users";
import Modules from "@/pages/admin/Modules";
import AuditLog from "@/pages/admin/AuditLog";

const FullLoader = () => (
  <div className="grid min-h-screen place-items-center bg-background">
    <div className="h-6 w-6 animate-spin rounded-full border-2 border-brand border-t-transparent" />
  </div>
);

const Protected: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user, loading } = useAuth();
  const location = useLocation();
  if (loading) return <FullLoader />;
  if (!user) return <Navigate to="/login" replace state={{ from: location }} />;
  return <>{children}</>;
};

const PublicOnly: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user, loading } = useAuth();
  if (loading) return <FullLoader />;
  if (user) return <Navigate to="/today" replace />;
  return <>{children}</>;
};

// Defense-in-depth presentation guard: authorization is always enforced server-side, but
// redirecting unauthorized users away from privileged routes keeps behaviour consistent and
// avoids firing permission-gated calls that would 403. Every role that sees the nav link for
// these routes also holds the permission, so no legitimate user is ever redirected.
const RequirePerm: React.FC<{ perm: string; children: React.ReactNode }> = ({ perm, children }) => {
  const { hasPerm } = useAuth();
  if (!hasPerm(perm)) return <Navigate to="/today" replace />;
  return <>{children}</>;
};

const MODULE_ROUTES = [
  "/more",
];

function App() {
  if (window.location.pathname === "/demo" || window.location.pathname.startsWith("/demo/")) {
    return (
      <BrowserRouter>
        <Routes>
          <Route path="/demo" element={<Demo />} />
          <Route path="/demo/:section" element={<Demo />} />
          <Route path="/demo/:section/:id" element={<Demo />} />
          <Route path="*" element={<Demo />} />
        </Routes>
      </BrowserRouter>
    );
  }
  return (
    <AuthProvider>
      <I18nProvider>
      <BrowserRouter>
        <Routes>
          <Route path="/login" element={<PublicOnly><Login /></PublicOnly>} />
          <Route path="/activate" element={<Activate />} />
          <Route element={<Protected><AppShell /></Protected>}>
            <Route path="/today" element={<Today />} />
            <Route path="/notifications" element={<Notifications />} />
            <Route path="/search" element={<Search />} />
            <Route path="/stores" element={<Stores />} />
            <Route path="/store/:storeId" element={<StoreDay />} />
            <Route path="/actions" element={<Actions />} />
            <Route path="/inbox" element={<Inbox />} />
            <Route path="/communicate" element={<Communications />} />
            <Route path="/knowledge" element={<Knowledge />} />
            <Route path="/execute" element={<Tasks />} />
            <Route path="/my-work" element={<MyWork />} />
            <Route path="/learn" element={<Learning />} />
            <Route path="/workforce" element={<Scheduling />} />
            <Route path="/team" element={<Scheduling />} />
            <Route path="/my-schedule" element={<MySchedule />} />
            <Route path="/my-day" element={<MyDay />} />
            <Route path="/ops-calendar" element={<UnifiedCalendar />} />
            <Route path="/conversations" element={<Conversations />} />
            <Route path="/forms" element={<RequirePerm perm="form.view"><Forms /></RequirePerm>} />
            <Route path="/freshness" element={<RequirePerm perm="freshness.view"><Freshness /></RequirePerm>} />
            <Route path="/labor-pool" element={<RequirePerm perm="wf.view"><LaborPool /></RequirePerm>} />
            <Route path="/capacity" element={<RequirePerm perm="capacity.view"><Capacity /></RequirePerm>} />
            <Route path="/operating-hours" element={<RequirePerm perm="wf.manage"><OperatingHours /></RequirePerm>} />
            <Route path="/time" element={<RequirePerm perm="ta.manage"><TimeAttendance /></RequirePerm>} />
            <Route path="/my-time" element={<MyTime />} />
            <Route path="/labor" element={<RequirePerm perm="lp.view"><LaborPlanning /></RequirePerm>} />
            <Route path="/forecast-adjustments" element={<RequirePerm perm="goal.view"><ForecastAdjustments /></RequirePerm>} />
            <Route path="/assure" element={<RequirePerm perm="assure.view"><Assure /></RequirePerm>} />
            <Route path="/intelligence" element={<RequirePerm perm="intel.view"><Intelligence /></RequirePerm>} />
            <Route path="/exceptions" element={<RequirePerm perm="intel.view"><Intelligence /></RequirePerm>} />
            <Route path="/support" element={<HelpDesk />} />
            <Route path="/assist" element={<Assist />} />
            <Route path="/connect" element={<Connect />} />
            <Route path="/platform" element={<Platform />} />
            <Route path="/history" element={<Actions />} />
            <Route path="/admin/hierarchy" element={<RequirePerm perm="org.view"><Hierarchy /></RequirePerm>} />
            <Route path="/admin/users" element={<Users />} />
            <Route path="/admin/modules" element={<RequirePerm perm="org.view"><Modules /></RequirePerm>} />
            <Route path="/admin/audit" element={<RequirePerm perm="audit.view"><AuditLog /></RequirePerm>} />
            {MODULE_ROUTES.map((p) => (
              <Route key={p} path={p} element={<Placeholder />} />
            ))}
          </Route>
          <Route path="*" element={<Navigate to="/today" replace />} />
        </Routes>
      </BrowserRouter>
      <Toaster position="top-right" richColors />
      </I18nProvider>
    </AuthProvider>
  );
}

export default App;
